import React, { useState, useEffect } from 'react';
import { Database, Plus, Trash2, Link, ShieldCheck, Loader2, Edit2, Save, X } from 'lucide-react';
import { fetchDataSources, createDataSource, updateDataSource, deleteDataSource } from '../services/dataSourceService';
import { DataSource } from '../types';

const DataSourceView: React.FC = () => {
  const [sources, setSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Form State
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [url, setUrl] = useState('');

  useEffect(() => { loadSources(); }, []);

  const loadSources = async () => {
    setLoading(true);
    try {
      const data = await fetchDataSources();
      setSources(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
      setIsCreating(false);
      setEditingId(null);
      setName('');
      setType('');
      setUrl('');
  };

  const handleEditClick = (source: DataSource) => {
      setEditingId(source.id);
      setName(source.name);
      setType(source.type);
      setUrl(source.website_url || '');
      setIsCreating(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCreate = async () => {
    if (!name || !type) return;
    try {
      await createDataSource(name, type, url);
      resetForm();
      loadSources();
    } catch (e) {
      alert("Failed to create source");
    }
  };

  const handleUpdate = async () => {
      if (!editingId || !name || !type) return;
      try {
          await updateDataSource(editingId, { name, type, website_url: url });
          resetForm();
          loadSources();
      } catch (e) {
          alert("Failed to update source");
      }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this source?")) return;
    try {
      await deleteDataSource(id);
      setSources(sources.filter(s => s.id !== id));
    } catch (e: any) {
      alert(e.message || "Failed to delete: Check if this source is in use.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-300">
      <div className="flex justify-between items-end border-b border-slate-700 pb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-white mb-2 flex items-center gap-3">
            <Database className="text-indigo-500" />
            Data Sources
          </h1>
          <p className="text-slate-400">
            Registry of verified institutions, reports, and websites feeding the platform.
          </p>
        </div>
        <button 
          onClick={() => { resetForm(); setIsCreating(true); }}
          className={`bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-opacity ${isCreating || editingId ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <Plus size={18} /> Add Source
        </button>
      </div>

      {(isCreating || editingId) && (
        <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-xl mb-8 animate-in slide-in-from-top-2">
          <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-white">{editingId ? 'Edit Source' : 'Register Source'}</h3>
              <button onClick={resetForm} className="text-slate-500 hover:text-white"><X size={20}/></button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-1">
              <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Name</label>
              <input 
                value={name} 
                onChange={e => setName(e.target.value)} 
                className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white focus:ring-2 focus:ring-indigo-500 outline-none" 
                placeholder="Ministry of ..." 
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Type</label>
              <select 
                value={type} 
                onChange={e => setType(e.target.value)} 
                className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="" disabled>Select Type</option>
                <option value="GOV">Government</option>
                <option value="INTL_ORG">International Org / NGO</option>
                <option value="PRIVATE_FIRM">Private Firm</option>
                <option value="NEWS">News Media</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Website URL</label>
              <input 
                value={url} 
                onChange={e => setUrl(e.target.value)} 
                className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white focus:ring-2 focus:ring-indigo-500 outline-none" 
                placeholder="https://" 
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={resetForm} className="text-slate-400 hover:text-white px-4 py-2">Cancel</button>
            <button 
                onClick={editingId ? handleUpdate : handleCreate} 
                className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2"
            >
                <Save size={18} /> {editingId ? 'Update' : 'Register'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-indigo-500" size={32}/></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sources.map(source => (
            <div key={source.id} className={`bg-slate-800 p-6 rounded-xl border hover:border-indigo-500 transition-colors group relative flex flex-col h-full ${editingId === source.id ? 'border-indigo-500 ring-1 ring-indigo-500' : 'border-slate-700'}`}>
                <div className="absolute top-4 right-4 flex gap-2 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                    <button 
                        onClick={() => handleEditClick(source)}
                        className="text-slate-600 hover:text-white p-1 rounded hover:bg-slate-700 transition-colors"
                        title="Edit"
                    >
                        <Edit2 size={16} />
                    </button>
                    <button 
                        onClick={() => handleDelete(source.id)} 
                        className="text-slate-600 hover:text-red-400 p-1 rounded hover:bg-slate-700 transition-colors"
                        title="Delete"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
                
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-slate-900 rounded flex items-center justify-center border border-slate-700 shrink-0">
                        <ShieldCheck className="text-emerald-400" size={20} />
                    </div>
                    <div className="overflow-hidden">
                        <h3 className="font-bold text-white text-lg leading-tight line-clamp-1" title={source.name}>{source.name}</h3>
                        <p className="text-xs text-slate-400">{source.type}</p>
                    </div>
                </div>
                
                <div className="mt-auto pt-4 border-t border-slate-700 flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-2">
                        <span className="bg-emerald-900/20 text-emerald-400 px-2 py-0.5 rounded border border-emerald-900/30 font-mono">{source.credibility}% Trust</span>
                    </div>
                    {source.website_url && (
                        <a href={source.website_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-indigo-400 transition-colors">
                            <Link size={12} /> Visit
                        </a>
                    )}
                </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DataSourceView;